import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

/**
 * Container for Row items — one bordered card whose rows share a single column
 * grid so every cell lines up down the list. Each <Row> is a `subgrid` spanning
 * these tracks, so badges, timestamps and actions form true aligned columns
 * (no <table>).
 *
 * The shared column grid only exists at `sm`+. On phones the columns would
 * crush the title down to a few characters, so below `sm` the list is a plain
 * stacked card and each Row lays itself out on two lines (see <Row>).
 *
 * Columns (sm+): [status] [title (flex)] [description] [badge] [timestamp] [actions]
 * A row without a description lets the title span both middle columns, so the
 * reserved description track never shows up as an empty hole in the line.
 */
export function RowList({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <ul
      className={`card divide-y divide-line overflow-hidden sm:grid sm:grid-cols-[auto_minmax(0,1fr)_minmax(0,14rem)_auto_auto_auto] sm:gap-x-3 ${className}`}
    >
      {children}
    </ul>
  );
}

/**
 * Title link styling. Only the title navigates — the row is NOT a stretched-link
 * click target, so tapping empty space, a badge or the timestamp does nothing.
 * `block truncate` makes the link fill its cell (a generous tap target that is
 * the full first line on mobile, the title column on desktop) and `hover:underline`
 * signals that the title — and only the title — is the link.
 */
const TITLE_LINK =
  'block truncate rounded-sm hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-signal/60';

/**
 * THE record row — issues, runs, loops, PRs, activity.
 *
 * On `sm`+ this is one dense, scannable line whose cells snap to the RowList's
 * shared columns (`grid-cols-subgrid`), so badges, timestamps and actions align
 * perfectly from row to row. The right columns name the state in words and stay
 * always visible — nothing hides behind hover, because a dense list you must
 * hover to read is a list you can't scan.
 *
 * Below `sm` the same cells restack onto two lines so nothing is crushed:
 *   line 1 — status + title (full width) + actions
 *   line 2 — badge (left) · timestamp (right)
 * The title is the only click target (see TITLE_LINK) — the row is not a
 * stretched link, so its accessible name stays the title and empty space,
 * badges and timestamps are inert.
 */
export function Row({
  to,
  href,
  status,
  badge,
  title,
  description,
  timestamp,
  actions,
  dense = false,
  className = '',
}: {
  to?: string;
  href?: string;
  /** Left rail, for row-level *controls* only — e.g. a selection checkbox.
   *  State belongs in `badge`, not here. */
  status?: ReactNode;
  /** The row's state, as a small <Badge>. Its own aligned column, always shown. */
  badge?: ReactNode;
  title: ReactNode;
  /** Muted description — its own aligned, truncating column (hidden on mobile). */
  description?: ReactNode;
  /** Muted timestamp — right-aligned column, always visible. */
  timestamp?: ReactNode;
  /** Row actions — collapse multiples into an OverflowMenu. Always visible. */
  actions?: ReactNode;
  dense?: boolean;
  className?: string;
}) {
  const interactive = Boolean(to || href);
  return (
    <li
      className={`group relative isolate grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 px-4 sm:col-span-full sm:grid-cols-subgrid sm:gap-y-0 ${
        dense ? 'py-1.5' : 'py-2'
      } ${interactive ? 'transition-colors hover:bg-surface-2' : ''} ${className}`}
    >
      {status && (
        <span className="col-start-1 row-span-2 flex items-center gap-2 self-center sm:row-span-1">
          {status}
        </span>
      )}

      <span
        className={`col-start-2 row-start-1 min-w-0 truncate text-sm font-medium leading-snug text-ink ${
          // Without a description the title claims that column too — otherwise
          // the reserved track reads as a weird empty gap in the row. col-end
          // (not col-span) because the span shorthand would override col-start.
          description ? '' : 'sm:col-end-4'
        }`}
      >
        {to ? (
          <Link to={to} className={TITLE_LINK}>
            {title}
          </Link>
        ) : href ? (
          <a href={href} target="_blank" rel="noreferrer noopener" className={TITLE_LINK}>
            {title}
          </a>
        ) : (
          title
        )}
      </span>

      {description && (
        <span className="col-start-3 hidden min-w-0 truncate text-xs text-ink-faint sm:block">
          {description}
        </span>
      )}

      {badge && (
        <span className="col-start-2 row-start-2 relative z-10 flex-none justify-self-start sm:col-start-4 sm:row-start-1">
          {badge}
        </span>
      )}

      {timestamp && (
        <span className="col-start-3 row-start-2 justify-self-end text-right text-2xs tabular-nums text-ink-faint sm:col-start-5 sm:row-start-1">
          {timestamp}
        </span>
      )}

      {actions && (
        <div className="col-start-3 row-start-1 relative z-10 flex items-center gap-1 justify-self-end sm:col-start-6">
          {actions}
        </div>
      )}
    </li>
  );
}
